import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

/** Admin pages are designed light-only: the shopper's dark theme (or the
 * device's dark preference) must never turn them black. */
export const isAdminPath = (pathname) => /^\/admin(\/|$)/.test(pathname || '');

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    let stored = null;
    try { stored = localStorage.getItem('theme'); } catch (e) { /* storage blocked */ }
    if (stored === 'dark' || stored === 'light') return stored;
    if (typeof window.matchMedia === 'function' && window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';
    return 'light';
  });

  const { pathname } = useLocation();
  const admin = isAdminPath(pathname);
  const applied = admin ? 'light' : theme;

  useEffect(() => {
    const root = document.documentElement;
    if (applied === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    // Native controls, scrollbars and the page background follow this too.
    root.style.colorScheme = admin ? 'light' : '';
    root.classList.toggle('admin-light', admin);
    document.body.style.background = admin ? '#FFFFFF' : '';
  }, [applied, admin]);

  useEffect(() => {
    try { localStorage.setItem('theme', theme); } catch (e) { /* storage blocked */ }
  }, [theme]);

  const toggleTheme = () => setTheme(prev => prev === 'light' ? 'dark' : 'light');

  return (
    <ThemeContext.Provider value={{ theme: applied, isDark: applied === 'dark', toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within ThemeProvider');
  return context;
};

export default ThemeContext;
